import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui'
import type { ParcelExtractionStatus } from '@/types'

interface ExtractionStatusChipProps {
  status: ParcelExtractionStatus | null
  extractedUnitNumber: string | null
}

/**
 * Renders an extraction-status Badge next to the unit field on the parcel
 * detail page.  Returns null for 'pending' (no badge) and when status is null.
 *
 * Colours follow the task spec:
 *   processing  → neutral (default)
 *   matched     → success  (pre-fill is handled in the calling page)
 *   needs_review → warning
 *   no_match    → error
 */
export function ExtractionStatusChip({
  status,
  extractedUnitNumber,
}: ExtractionStatusChipProps) {
  const { t } = useTranslation('parcels')

  if (!status || status === 'pending') return null

  switch (status) {
    case 'processing':
      return <Badge variant="default">{t('extraction.reading_unit')}</Badge>

    case 'matched':
      return (
        <Badge variant="success">
          {t('extraction.unit_matched', { number: extractedUnitNumber ?? '' })}
        </Badge>
      )

    case 'needs_review':
      return <Badge variant="warning">{t('extraction.confirm_unit')}</Badge>

    case 'no_match':
      return <Badge variant="error">{t('extraction.no_match')}</Badge>

    default:
      return null
  }
}
